import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useOptimizedQuery, createSupabaseQuery } from "@/hooks/useOptimizedQuery";

export interface Notification {
  id: string;
  type: 'invite' | 'task' | 'queue' | 'system';
  title: string;
  message: string;
  read: boolean;
  created_at: string;
  action_url?: string;
  metadata?: any;
  created_by_user_id?: string;
}

export const useNotifications = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const fetchNotifications = createSupabaseQuery<Notification[]>(
    () => supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user?.id || '')
      .order('created_at', { ascending: false })
      .limit(50),
    (data) => data || []
  );

  const {
    data: notifications = [],
    isLoading: loading,
    refetch
  } = useOptimizedQuery<Notification[]>({
    queryKey: ['notifications', user?.id],
    queryFn: fetchNotifications,
    enabled: !!user,
  });

  const markAsRead = async (id: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', id);

      if (error) throw error;
      
      // Update React Query cache
      queryClient.setQueryData(['notifications', user?.id], (old: Notification[] = []) =>
        old.map(notification => 
          notification.id === id 
            ? { ...notification, read: true }
            : notification
        )
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', user.id)
        .eq('read', false);

      if (error) throw error;
      
      // Update React Query cache
      queryClient.setQueryData(['notifications', user.id], (old: Notification[] = []) =>
        old.map(notification => ({ ...notification, read: true }))
      );
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const clearAllNotifications = async () => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('user_id', user.id);

      if (error) throw error;
      
      // Update React Query cache
      queryClient.setQueryData(['notifications', user.id], []);
    } catch (error) {
      console.error('Error clearing all notifications:', error);
    }
  };

  const createNotification = async (notification: Omit<Notification, 'id' | 'created_at' | 'read'> & { user_id: string }) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .insert([notification]);

      if (error) throw error;
    } catch (error) {
      console.error('Error creating notification:', error);
    }
  };

  // Set up real-time subscription for new and updated notifications
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('New notification received:', payload.new);
          // Update React Query cache
          queryClient.setQueryData(['notifications', user.id], (old: Notification[] = []) => {
            const newNotif = payload.new as Notification;
            if (old.some(n => n.id === newNotif.id)) return old;
            return [newNotif, ...old];
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('Notification updated:', payload.new);
          // Update React Query cache
          queryClient.setQueryData(['notifications', user.id], (old: Notification[] = []) =>
            old.map(notification => 
              notification.id === (payload.new as Notification).id 
                ? payload.new as Notification
                : notification
            )
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const unreadCount = notifications.filter(n => !n.read).length;

  return {
    notifications,
    loading,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearAllNotifications,
    createNotification,
    refetch
  };
};